// Medical Scores Database - Part 3: Additional Specialties
// Scores for Oncology, Orthopedics, Rheumatology, Toxicology, Obstetrics, Hematology

import {
  Brain,
  Heart,
  Activity,
  Baby,
  Thermometer,
  Droplets,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Beaker,
  User,
  Zap,
  Target,
  Shield
} from 'lucide-react';

// ==================== ONCOLOGIE ====================
export const ONCOLOGY_SCORES = {
  karnofsky: {
    name: 'Échelle de Karnofsky',
    category: 'Oncologie',
    icon: Target,
    description: 'État général avant chimiothérapie',
    color: 'fuchsia',
    fields: [
      { id: 'score', label: 'Performance Status', type: 'select', options: [
        { value: 100, label: '100 - Normal, pas de plainte' },
        { value: 90, label: '90 - Activité normale, symptômes mineurs' },
        { value: 80, label: '80 - Activité normale avec effort, quelques symptômes' },
        { value: 70, label: '70 - Incapable de travailler, soins personnels seuls' },
        { value: 60, label: '60 - Aide occasionnelle, soins personnels' },
        { value: 50, label: '50 - Aide considérable, soins médicaux fréquents' },
        { value: 40, label: '40 - Handicapé, soins spéciaux' },
        { value: 30, label: '30 - Sévèrement handicapé, hospitalisation' },
        { value: 20, label: '20 - Très malade, hospitalisation nécessaire' },
        { value: 10, label: '10 - Moribond' },
        { value: 0, label: '0 - Décédé' }
      ]}
    ],
    calculate: (values) => ({ score: values.score || 100, max: 100 }),
    interpret: (score) => {
      if (score >= 80) return { level: 'success', text: `Karnofsky ${score}%`, detail: 'Bon état général, chimiothérapie standard possible' };
      if (score >= 60) return { level: 'warning', text: `Karnofsky ${score}%`, detail: 'État général altéré, adapter chimiothérapie' };
      if (score >= 40) return { level: 'danger', text: `Karnofsky ${score}%`, detail: 'État général médiocre, chimiothérapie à discuter' };
      return { level: 'danger', text: `Karnofsky ${score}%`, detail: 'État général très altéré, soins de support' };
    },
    formula: 'Karnofsky 0-100%: performance status en oncologie'
  },

  ecog: {
    name: 'ECOG Performance Status',
    category: 'Oncologie',
    icon: Target,
    description: 'État de santé global',
    color: 'fuchsia',
    fields: [
      { id: 'score', label: 'ECOG PS', type: 'select', options: [
        { value: 0, label: '0 - Activité normale' },
        { value: 1, label: '1 - Restriction activité physique intense' },
        { value: 2, label: '2 - Alité < 50% du temps' },
        { value: 3, label: '3 - Alité > 50% du temps' },
        { value: 4, label: '4 - Grabataire' },
        { value: 5, label: '5 - Décédé' }
      ]}
    ],
    calculate: (values) => ({ score: values.score || 0, max: 5 }),
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `ECOG ${score}`, detail: 'Chimiothérapie standard possible' };
      if (score === 2) return { level: 'warning', text: `ECOG ${score}`, detail: 'Chimiothérapie adaptée' };
      if (score === 3) return { level: 'danger', text: `ECOG ${score}`, detail: 'Chimiothérapie à discuter, soins palliatifs' };
      return { level: 'danger', text: `ECOG ${score}`, detail: 'Soins palliatifs exclusifs' };
    },
    formula: 'ECOG 0-5: Eastern Cooperative Oncology Group'
  },

  g8: {
    name: 'Questionnaire G8',
    category: 'Oncologie',
    icon: Target,
    description: 'Dépistage fragilité gériatrique en oncologie',
    color: 'fuchsia',
    fields: [
      { id: 'appetite', label: 'Appétit diminué', type: 'select', options: [
        { value: 0, label: '0 - Anorexie sévère' }, { value: 1, label: '1 - Modérée' }, { value: 2, label: '2 - Pas de problème' }
      ]},
      { id: 'weightLoss', label: 'Perte de poids (3 mois)', type: 'select', options: [
        { value: 0, label: '0 - > 3 kg' }, { value: 1, label: '1 - Ne sait pas' },
        { value: 2, label: '2 - 1-3 kg' }, { value: 3, label: '3 - Pas de perte' }
      ]},
      { id: 'mobility', label: 'Mobilité', type: 'select', options: [
        { value: 0, label: '0 - Alité' }, { value: 1, label: '1 - Se lève' }, { value: 2, label: '2 - Sort' }
      ]},
      { id: 'neuropsych', label: 'Problèmes neuropsychiatriques', type: 'select', options: [
        { value: 0, label: '0 - Démence/dépression sévère' }, { value: 1, label: '1 - Légère' }, { value: 2, label: '2 - Non' }
      ]},
      { id: 'bmi', label: 'IMC', type: 'select', options: [
        { value: 0, label: '0 - < 19' }, { value: 1, label: '1 - 19-20.9' },
        { value: 2, label: '2 - 21-22.9' }, { value: 3, label: '3 - ≥ 23' }
      ]},
      { id: 'medications', label: '> 3 médicaments/jour', type: 'select', options: [
        { value: 0, label: '0 - Oui' }, { value: 1, label: '1 - Non' }
      ]},
      { id: 'health', label: 'État de santé perçu', type: 'select', options: [
        { value: 0, label: '0 - Moins bon' }, { value: 0.5, label: '0.5 - Ne sait pas' },
        { value: 1, label: '1 - Aussi bon' }, { value: 2, label: '2 - Meilleur' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '0 - > 85 ans' }, { value: 1, label: '1 - 80-85' }, { value: 2, label: '2 - < 80' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 17 };
    },
    interpret: (score) => {
      if (score > 14) return { level: 'success', text: `G8: ${score}/17`, detail: 'Pas de fragilité. Traitement standard' };
      return { level: 'warning', text: `G8: ${score}/17`, detail: 'Fragilité probable. Évaluation gériatrique recommandée' };
    },
    formula: 'G8 ≤ 14 = indication évaluation gériatrique'
  },

  mascc: {
    name: 'Score MASCC',
    category: 'Oncologie',
    icon: Thermometer,
    description: 'Risque neutropénie fébrile',
    color: 'fuchsia',
    fields: [
      { id: 'severity', label: 'Sévérité maladie', type: 'select', options: [
        { value: 5, label: '5 - Symptômes absents ou légers' },
        { value: 3, label: '3 - Symptômes modérés' },
        { value: 0, label: '0 - Symptômes sévères' }
      ]},
      { id: 'hypotension', label: 'Pas d\'hypotension', type: 'select', options: [
        { value: 5, label: '5 - Oui' }, { value: 0, label: '0 - Hypotension' }
      ]},
      { id: 'copd', label: 'Pas de BPCO', type: 'select', options: [
        { value: 4, label: '4 - Oui' }, { value: 0, label: '0 - BPCO' }
      ]},
      { id: 'tumor', label: 'Tumeur solide ou pas d\'infection fongique', type: 'select', options: [
        { value: 4, label: '4 - Oui' }, { value: 0, label: '0 - Non' }
      ]},
      { id: 'dehydration', label: 'Pas de déshydratation', type: 'select', options: [
        { value: 3, label: '3 - Oui' }, { value: 0, label: '0 - Déshydratation' }
      ]},
      { id: 'outpatient', label: 'Début fébrile en ambulatoire', type: 'select', options: [
        { value: 3, label: '3 - Oui' }, { value: 0, label: '0 - Hospitalisé' }
      ]},
      { id: 'age', label: 'Âge < 60 ans', type: 'select', options: [
        { value: 2, label: '2 - Oui' }, { value: 0, label: '0 - ≥ 60 ans' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 26 };
    },
    interpret: (score) => {
      if (score >= 21) return { level: 'success', text: `MASCC ${score}`, detail: 'Risque faible. Traitement ambulatoire possible' };
      return { level: 'danger', text: `MASCC ${score}`, detail: 'Risque élevé. Hospitalisation recommandée' };
    },
    formula: 'MASCC ≥ 21 = risque faible de complications'
  },

  surfaceCorporelle: {
    name: 'Surface Corporelle',
    category: 'Oncologie',
    icon: Scale,
    description: 'Calcul SC pour chimiothérapie',
    color: 'fuchsia',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 20, max: 250 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 100, max: 250 }
    ],
    calculate: (values) => {
      const w = values.weight || 70;
      const h = values.height || 170;
      // Formule de Mosteller
      const sc = Math.sqrt((w * h) / 3600);
      return { score: sc.toFixed(2), max: 'm²' };
    },
    interpret: (score) => {
      return { level: 'success', text: `SC: ${score} m²`, detail: 'Utiliser pour le calcul des doses de chimiothérapie' };
    },
    formula: 'SC = √(Poids × Taille / 3600) [Mosteller]'
  }
};

// ==================== ORTHOPÉDIE ====================
export const ORTHOPEDIC_SCORES = {
  ottawaAnkle: {
    name: 'Règles d\'Ottawa (Cheville)',
    category: 'Orthopédie',
    icon: Bone,
    description: 'Indication radio cheville/pied',
    color: 'stone',
    fields: [
      { id: 'malleolus', label: 'Douleur malléole postérieure (6 cm)', type: 'checkbox', points: 1 },
      { id: 'fibula', label: 'Douleur pointe malléole externe', type: 'checkbox', points: 1 },
      { id: 'tibia', label: 'Douleur pointe malléole interne', type: 'checkbox', points: 1 },
      { id: 'navicular', label: 'Douleur base 5e métatarsien', type: 'checkbox', points: 1 },
      { id: 'midfoot', label: 'Douleur scaphoïde tarsien', type: 'checkbox', points: 1 },
      { id: 'weightBearing', label: 'Incapacité de faire 4 pas', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Ottawa négatif', detail: 'Radio non indiquée (Se 98%)' };
      return { level: 'warning', text: 'Ottawa positif', detail: 'Radiographie recommandée' };
    },
    formula: 'Radio si ≥ 1 critère positif'
  },

  ottawaKnee: {
    name: 'Règles d\'Ottawa (Genou)',
    category: 'Orthopédie',
    icon: Bone,
    description: 'Indication radio genou',
    color: 'stone',
    fields: [
      { id: 'age55', label: 'Âge ≥ 55 ans', type: 'checkbox', points: 1 },
      { id: 'fibula', label: 'Douleur isolée rotule', type: 'checkbox', points: 1 },
      { id: 'fibulaHead', label: 'Douleur tête péroné', type: 'checkbox', points: 1 },
      { id: 'flexion', label: 'Incapacité flexion 90°', type: 'checkbox', points: 1 },
      { id: 'weightBearing', label: 'Incapacité faire 4 pas', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Ottawa négatif', detail: 'Radio non indiquée' };
      return { level: 'warning', text: 'Ottawa positif', detail: 'Radiographie recommandée' };
    },
    formula: 'Radio si ≥ 1 critère positif'
  },

  lequesneHip: {
    name: 'Indice de Lequesne (Hanche)',
    category: 'Orthopédie',
    icon: Bone,
    description: 'Sévérité coxarthrose',
    color: 'stone',
    fields: [
      { id: 'nightPain', label: 'Douleur nocturne', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Au mouvement' }, { value: 2, label: '2 - Au repos' }
      ]},
      { id: 'morningStiffness', label: 'Dérouillage matinal', type: 'select', options: [
        { value: 0, label: '0 - < 1 min' }, { value: 1, label: '1 - 1-15 min' }, { value: 2, label: '2 - > 15 min' }
      ]},
      { id: 'walkingPain', label: 'Douleur à la marche', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Après une certaine distance' },
        { value: 2, label: '2 - Précoce et croissante' }
      ]},
      { id: 'walkingDistance', label: 'Distance de marche max', type: 'select', options: [
        { value: 0, label: '0 - Illimitée' }, { value: 1, label: '1 - > 1 km' }, { value: 2, label: '2 - 500m-1km' },
        { value: 3, label: '3 - 300-500m' }, { value: 4, label: '4 - 100-300m' },
        { value: 5, label: '5 - < 100m' }, { value: 6, label: '6 - Avec canne' }, { value: 7, label: '7 - Avec 2 cannes' }
      ]},
      { id: 'stairs', label: 'Monter escalier', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Avec difficulté' }, { value: 2, label: '2 - Impossible' }
      ]},
      { id: 'squat', label: 'S\'accroupir', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Avec difficulté' }, { value: 2, label: '2 - Impossible' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 24 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `Lequesne ${score}`, detail: 'Handicap minime' };
      if (score <= 7) return { level: 'warning', text: `Lequesne ${score}`, detail: 'Handicap modéré' };
      if (score <= 10) return { level: 'warning', text: `Lequesne ${score}`, detail: 'Handicap important' };
      if (score <= 13) return { level: 'danger', text: `Lequesne ${score}`, detail: 'Handicap très important' };
      return { level: 'danger', text: `Lequesne ${score}`, detail: 'Handicap extrêmement important. PTH à discuter' };
    },
    formula: 'Lequesne: ≥ 10-12 = indication chirurgie'
  },

  salterHarris: {
    name: 'Classification Salter-Harris',
    category: 'Orthopédie',
    icon: Baby,
    description: 'Fractures pédiatriques (cartilage)',
    color: 'stone',
    fields: [
      { id: 'type', label: 'Type de fracture', type: 'select', options: [
        { value: 1, label: 'Type I - Décollement épiphysaire pur' },
        { value: 2, label: 'Type II - Décollement + fragment métaphysaire' },
        { value: 3, label: 'Type III - Fracture épiphysaire intra-articulaire' },
        { value: 4, label: 'Type IV - Fracture métaphyso-épiphysaire' },
        { value: 5, label: 'Type V - Écrasement du cartilage de croissance' }
      ]}
    ],
    calculate: (values) => ({ score: values.type || 1, max: 5 }),
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Salter-Harris ${score}`, detail: 'Bon pronostic. Traitement orthopédique souvent suffisant' };
      if (score <= 4) return { level: 'warning', text: `Salter-Harris ${score}`, detail: 'Atteinte articulaire. Réduction anatomique nécessaire' };
      return { level: 'danger', text: `Salter-Harris ${score}`, detail: 'Risque d\'épiphysiodèse. Surveillance croissance +++' };
    },
    formula: 'SALTR: Straight across (I), Above (II), Lower (III), Through (IV), Rammed (V)'
  }
};

// ==================== RHUMATOLOGIE ====================
export const RHEUMATOLOGY_SCORES = {
  das28: {
    name: 'DAS 28',
    category: 'Rhumatologie',
    icon: Bone,
    description: 'Activité polyarthrite rhumatoïde',
    color: 'lime',
    fields: [
      { id: 'swollenJoints', label: 'Articulations gonflées (0-28)', type: 'number', min: 0, max: 28 },
      { id: 'tenderJoints', label: 'Articulations douloureuses (0-28)', type: 'number', min: 0, max: 28 },
      { id: 'esr', label: 'VS (mm/h)', type: 'number', min: 1, max: 150 },
      { id: 'patientVas', label: 'EVA patient (0-100)', type: 'number', min: 0, max: 100 }
    ],
    calculate: (values) => {
      const swj = values.swollenJoints || 0;
      const tj = values.tenderJoints || 0;
      const esr = values.esr || 20;
      const vas = values.patientVas || 50;
      
      const das28 = 0.56 * Math.sqrt(tj) + 0.28 * Math.sqrt(swj) + 0.70 * Math.log(esr) + 0.014 * vas;
      return { score: das28.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 2.6) return { level: 'success', text: `DAS28: ${score}`, detail: 'Rémission' };
      if (s < 3.2) return { level: 'success', text: `DAS28: ${score}`, detail: 'Faible activité' };
      if (s < 5.1) return { level: 'warning', text: `DAS28: ${score}`, detail: 'Activité modérée' };
      return { level: 'danger', text: `DAS28: ${score}`, detail: 'Activité forte. Intensifier traitement' };
    },
    formula: 'DAS28 = 0.56×√NAD + 0.28×√NAG + 0.70×ln(VS) + 0.014×EVA'
  },

  basdai: {
    name: 'BASDAI',
    category: 'Rhumatologie',
    icon: Bone,
    description: 'Activité spondylarthrite',
    color: 'lime',
    fields: [
      { id: 'q1', label: 'Fatigue générale (0-10)', type: 'number', min: 0, max: 10, step: 0.1 },
      { id: 'q2', label: 'Douleur rachis/hanches (0-10)', type: 'number', min: 0, max: 10, step: 0.1 },
      { id: 'q3', label: 'Douleur/gonflement articulaire (0-10)', type: 'number', min: 0, max: 10, step: 0.1 },
      { id: 'q4', label: 'Gêne zones douloureuses au toucher (0-10)', type: 'number', min: 0, max: 10, step: 0.1 },
      { id: 'q5', label: 'Intensité raideur matinale (0-10)', type: 'number', min: 0, max: 10, step: 0.1 },
      { id: 'q6', label: 'Durée raideur matinale (0-10)', type: 'number', min: 0, max: 10, step: 0.1 }
    ],
    calculate: (values) => {
      const q1 = values.q1 || 0;
      const q2 = values.q2 || 0;
      const q3 = values.q3 || 0;
      const q4 = values.q4 || 0;
      const q5 = values.q5 || 0;
      const q6 = values.q6 || 0;
      const basdai = (q1 + q2 + q3 + q4 + (q5 + q6) / 2) / 5;
      return { score: basdai.toFixed(1), max: 10 };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 4) return { level: 'success', text: `BASDAI: ${score}/10`, detail: 'Activité faible' };
      return { level: 'warning', text: `BASDAI: ${score}/10`, detail: 'Activité élevée. Biothérapie à discuter si échec AINS' };
    },
    formula: 'BASDAI = (Q1+Q2+Q3+Q4+(Q5+Q6)/2) / 5'
  },

  haq: {
    name: 'HAQ (Health Assessment Questionnaire)',
    category: 'Rhumatologie',
    icon: Bone,
    description: 'Handicap fonctionnel PR',
    color: 'lime',
    fields: [
      { id: 'dressing', label: 'S\'habiller', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' }, { value: 1, label: '1 - Quelques difficultés' },
        { value: 2, label: '2 - Beaucoup de difficultés' }, { value: 3, label: '3 - Incapable' }
      ]},
      { id: 'rising', label: 'Se lever', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'eating', label: 'Manger', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'walking', label: 'Marcher', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'hygiene', label: 'Hygiène', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'reach', label: 'Atteindre', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'grip', label: 'Préhension', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'activities', label: 'Autres activités', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      const haq = total / 8;
      return { score: haq.toFixed(2), max: 3 };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 0.5) return { level: 'success', text: `HAQ: ${score}`, detail: 'Handicap minime' };
      if (s < 1.25) return { level: 'warning', text: `HAQ: ${score}`, detail: 'Handicap modéré' };
      if (s < 2) return { level: 'warning', text: `HAQ: ${score}`, detail: 'Handicap important' };
      return { level: 'danger', text: `HAQ: ${score}`, detail: 'Handicap sévère' };
    },
    formula: 'HAQ = Moyenne des 8 catégories (0-3)'
  }
};

// ==================== TOXICOLOGIE ====================
export const TOXICOLOGY_SCORES = {
  pss: {
    name: 'Poisoning Severity Score',
    category: 'Toxicologie',
    icon: Pill,
    description: 'Gravité intoxication',
    color: 'rose',
    fields: [
      { id: 'grade', label: 'Grade PSS', type: 'select', options: [
        { value: 0, label: '0 - Aucun symptôme' },
        { value: 1, label: '1 - Mineur (symptômes légers, transitoires)' },
        { value: 2, label: '2 - Modéré (symptômes prononcés)' },
        { value: 3, label: '3 - Sévère (symptômes graves, menaçant le pronostic vital)' },
        { value: 4, label: '4 - Fatal' }
      ]}
    ],
    calculate: (values) => ({ score: values.grade || 0, max: 4 }),
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'PSS 0', detail: 'Pas de symptôme' };
      if (score === 1) return { level: 'success', text: 'PSS 1', detail: 'Intoxication mineure. Surveillance simple' };
      if (score === 2) return { level: 'warning', text: 'PSS 2', detail: 'Intoxication modérée. Surveillance rapprochée' };
      if (score === 3) return { level: 'danger', text: 'PSS 3', detail: 'Intoxication sévère. Réanimation' };
      return { level: 'danger', text: 'PSS 4', detail: 'Décès' };
    },
    formula: 'PSS 0-4: classification OMS intoxications'
  },

  cushman: {
    name: 'Score de Cushman',
    category: 'Toxicologie',
    icon: Pill,
    description: 'Sévérité sevrage alcoolique',
    color: 'rose',
    fields: [
      { id: 'pulse', label: 'Pouls', type: 'select', options: [
        { value: 0, label: '0 - < 80/min' }, { value: 1, label: '1 - 80-100' },
        { value: 2, label: '2 - 101-120' }, { value: 3, label: '3 - > 120' }
      ]},
      { id: 'sbp', label: 'PAS', type: 'select', options: [
        { value: 0, label: '0 - < 135' }, { value: 1, label: '1 - 135-150' },
        { value: 2, label: '2 - 151-170' }, { value: 3, label: '3 - > 170' }
      ]},
      { id: 'rr', label: 'Fréquence respiratoire', type: 'select', options: [
        { value: 0, label: '0 - < 16' }, { value: 1, label: '1 - 16-24' },
        { value: 2, label: '2 - 25-35' }, { value: 3, label: '3 - > 35' }
      ]},
      { id: 'tremor', label: 'Tremblement', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Mains' },
        { value: 2, label: '2 - Membres supérieurs' }, { value: 3, label: '3 - Généralisé' }
      ]},
      { id: 'sweating', label: 'Sueurs', type: 'select', options: [
        { value: 0, label: '0 - Absentes' }, { value: 1, label: '1 - Paumes' },
        { value: 2, label: '2 - Front + paumes' }, { value: 3, label: '3 - Généralisées' }
      ]},
      { id: 'agitation', label: 'Agitation', type: 'select', options: [
        { value: 0, label: '0 - Calme' }, { value: 1, label: '1 - Agitation modérée' },
        { value: 2, label: '2 - Agitation importante' }, { value: 3, label: '3 - Agitation extrême' }
      ]},
      { id: 'orientation', label: 'Orientation', type: 'select', options: [
        { value: 0, label: '0 - Normale' }, { value: 1, label: '1 - Temps incertain' },
        { value: 2, label: '2 - Lieu incertain' }, { value: 3, label: '3 - Désorienté' }
      ]},
      { id: 'hallucinations', label: 'Troubles sensoriels', type: 'select', options: [
        { value: 0, label: '0 - Absents' }, { value: 1, label: '1 - Illusions' },
        { value: 2, label: '2 - Hallucinations critiquées' }, { value: 3, label: '3 - Hallucinations vraies' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 24 };
    },
    interpret: (score) => {
      if (score < 7) return { level: 'success', text: `Cushman ${score}`, detail: 'Sevrage léger. Benzodiazépines PO' };
      if (score < 14) return { level: 'warning', text: `Cushman ${score}`, detail: 'Sevrage modéré. Benzodiazépines, surveillance' };
      return { level: 'danger', text: `Cushman ${score}`, detail: 'Sevrage sévère/DT. Réanimation, BZD IV' };
    },
    formula: 'Cushman = 8 items × 0-3 (> 14 = DT imminent)'
  },

  fagerstrom: {
    name: 'Test de Fagerström',
    category: 'Toxicologie',
    icon: Pill,
    description: 'Dépendance nicotinique',
    color: 'rose',
    fields: [
      { id: 'morning', label: 'Délai 1ère cigarette après réveil', type: 'select', options: [
        { value: 3, label: '3 - ≤ 5 minutes' }, { value: 2, label: '2 - 6-30 minutes' },
        { value: 1, label: '1 - 31-60 minutes' }, { value: 0, label: '0 - > 60 minutes' }
      ]},
      { id: 'forbidden', label: 'Difficulté à ne pas fumer en lieu interdit', type: 'select', options: [
        { value: 1, label: '1 - Oui' }, { value: 0, label: '0 - Non' }
      ]},
      { id: 'hardest', label: 'Cigarette la plus difficile à supprimer', type: 'select', options: [
        { value: 1, label: '1 - La première le matin' }, { value: 0, label: '0 - Une autre' }
      ]},
      { id: 'number', label: 'Nombre de cigarettes/jour', type: 'select', options: [
        { value: 0, label: '0 - ≤ 10' }, { value: 1, label: '1 - 11-20' },
        { value: 2, label: '2 - 21-30' }, { value: 3, label: '3 - > 30' }
      ]},
      { id: 'morningMore', label: 'Fume plus le matin', type: 'select', options: [
        { value: 1, label: '1 - Oui' }, { value: 0, label: '0 - Non' }
      ]},
      { id: 'sick', label: 'Fume même malade alité', type: 'select', options: [
        { value: 1, label: '1 - Oui' }, { value: 0, label: '0 - Non' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Fagerström ${score}`, detail: 'Dépendance faible. Aide comportementale' };
      if (score <= 4) return { level: 'warning', text: `Fagerström ${score}`, detail: 'Dépendance moyenne. Substitution nicotinique' };
      if (score <= 6) return { level: 'warning', text: `Fagerström ${score}`, detail: 'Dépendance forte. Substitution + aide' };
      return { level: 'danger', text: `Fagerström ${score}`, detail: 'Dépendance très forte. Traitement intensif' };
    },
    formula: 'Fagerström 0-10: dépendance physique à la nicotine'
  },

  paquetsAnnees: {
    name: 'Paquets-années',
    category: 'Toxicologie',
    icon: Pill,
    description: 'Quantification exposition tabac',
    color: 'rose',
    fields: [
      { id: 'cigarettesPerDay', label: 'Cigarettes par jour', type: 'number', min: 1, max: 100 },
      { id: 'years', label: 'Années de tabagisme', type: 'number', min: 1, max: 80 }
    ],
    calculate: (values) => {
      const cigs = values.cigarettesPerDay || 20;
      const years = values.years || 10;
      const pa = (cigs / 20) * years;
      return { score: pa.toFixed(1), max: 'PA' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 10) return { level: 'success', text: `${score} PA`, detail: 'Exposition modérée' };
      if (s < 20) return { level: 'warning', text: `${score} PA`, detail: 'Exposition significative' };
      return { level: 'danger', text: `${score} PA`, detail: 'Exposition importante. Dépistage BPCO/cancer' };
    },
    formula: 'PA = (Cigarettes/jour ÷ 20) × Années'
  }
};

// ==================== OBSTÉTRIQUE ====================
export const OBSTETRIC_SCORES = {
  bishop: {
    name: 'Score de Bishop',
    category: 'Obstétrique',
    icon: Baby,
    description: 'Pronostic accouchement / Maturité col',
    color: 'pink',
    fields: [
      { id: 'dilation', label: 'Dilatation (cm)', type: 'select', options: [
        { value: 0, label: '0 - Fermé' }, { value: 1, label: '1 - 1-2 cm' },
        { value: 2, label: '2 - 3-4 cm' }, { value: 3, label: '3 - ≥ 5 cm' }
      ]},
      { id: 'effacement', label: 'Effacement (%)', type: 'select', options: [
        { value: 0, label: '0 - 0-30%' }, { value: 1, label: '1 - 40-50%' },
        { value: 2, label: '2 - 60-70%' }, { value: 3, label: '3 - ≥ 80%' }
      ]},
      { id: 'station', label: 'Hauteur présentation', type: 'select', options: [
        { value: 0, label: '0 - -3' }, { value: 1, label: '1 - -2' },
        { value: 2, label: '2 - -1/0' }, { value: 3, label: '3 - +1/+2' }
      ]},
      { id: 'consistency', label: 'Consistance col', type: 'select', options: [
        { value: 0, label: '0 - Ferme' }, { value: 1, label: '1 - Moyen' }, { value: 2, label: '2 - Mou' }
      ]},
      { id: 'position', label: 'Position col', type: 'select', options: [
        { value: 0, label: '0 - Postérieur' }, { value: 1, label: '1 - Moyen' }, { value: 2, label: '2 - Antérieur' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 13 };
    },
    interpret: (score) => {
      if (score >= 9) return { level: 'success', text: `Bishop ${score}`, detail: 'Col très favorable. Déclenchement possible' };
      if (score >= 6) return { level: 'success', text: `Bishop ${score}`, detail: 'Col favorable. Déclenchement envisageable' };
      if (score >= 3) return { level: 'warning', text: `Bishop ${score}`, detail: 'Col défavorable. Maturation nécessaire' };
      return { level: 'danger', text: `Bishop ${score}`, detail: 'Col très défavorable. Maturation prolongée' };
    },
    formula: 'Bishop = Dilatation + Effacement + Station + Consistance + Position'
  },

  apgarObs: {
    name: 'Score d\'Apgar (Obstétrique)',
    category: 'Obstétrique',
    icon: Baby,
    description: 'État nouveau-né à M1, M5, M10',
    color: 'pink',
    fields: [
      { id: 'hr', label: 'Fréquence cardiaque', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - < 100/min' }, { value: 2, label: '2 - ≥ 100/min' }
      ]},
      { id: 'resp', label: 'Respiration', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Irrégulière/lente' }, { value: 2, label: '2 - Cri vigoureux' }
      ]},
      { id: 'tone', label: 'Tonus', type: 'select', options: [
        { value: 0, label: '0 - Flasque' }, { value: 1, label: '1 - Faible' }, { value: 2, label: '2 - Actif' }
      ]},
      { id: 'reflex', label: 'Réactivité', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Grimace' }, { value: 2, label: '2 - Cri/toux' }
      ]},
      { id: 'color', label: 'Coloration', type: 'select', options: [
        { value: 0, label: '0 - Cyanose/pâleur' }, { value: 1, label: '1 - Acrocyanose' }, { value: 2, label: '2 - Rose' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score >= 7) return { level: 'success', text: `Apgar ${score}/10`, detail: 'Bonne adaptation' };
      if (score >= 4) return { level: 'warning', text: `Apgar ${score}/10`, detail: 'Dépression modérée. Stimulation, O₂' };
      return { level: 'danger', text: `Apgar ${score}/10`, detail: 'Dépression sévère. Réanimation' };
    },
    formula: 'Apgar = Apparence + Pouls + Grimace + Activité + Respiration'
  },

  malinas: {
    name: 'Score de Malinas',
    category: 'Obstétrique',
    icon: Baby,
    description: 'Temps restant avant accouchement',
    color: 'pink',
    fields: [
      { id: 'parity', label: 'Parité', type: 'select', options: [
        { value: 0, label: '0 - Primipare' }, { value: 1, label: '1 - 2e pare' }, { value: 2, label: '2 - ≥ 3e pare' }
      ]},
      { id: 'labor', label: 'Durée du travail', type: 'select', options: [
        { value: 0, label: '0 - < 3h' }, { value: 1, label: '1 - 3-5h' }, { value: 2, label: '2 - > 5h' }
      ]},
      { id: 'contractions', label: 'Contractions', type: 'select', options: [
        { value: 0, label: '0 - < 1/5 min' }, { value: 1, label: '1 - 1/3-5 min' }, { value: 2, label: '2 - > 1/3 min' }
      ]},
      { id: 'dilation', label: 'Dilatation', type: 'select', options: [
        { value: 0, label: '0 - < 4 cm' }, { value: 1, label: '1 - 4-7 cm' }, { value: 2, label: '2 - ≥ 8 cm' }
      ]},
      { id: 'membranes', label: 'Membranes', type: 'select', options: [
        { value: 0, label: '0 - Intactes' }, { value: 1, label: '1 - Bombent' }, { value: 2, label: '2 - Rompues' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `Malinas ${score}`, detail: 'Temps suffisant. Transport possible' };
      if (score <= 6) return { level: 'warning', text: `Malinas ${score}`, detail: 'Accouchement proche. Transport rapide' };
      return { level: 'danger', text: `Malinas ${score}`, detail: 'Accouchement imminent. Préparer sur place' };
    },
    formula: 'Malinas ≥ 7 = accouchement imminent'
  }
};

// ==================== HÉMATOLOGIE ====================
export const HEMATOLOGY_SCORES = {
  fourT: {
    name: 'Score 4T',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Risque TIH (thrombopénie induite héparine)',
    color: 'red',
    fields: [
      { id: 'thrombocytopenia', label: 'Thrombopénie', type: 'select', options: [
        { value: 2, label: '2 - Chute > 50% ou nadir 20-100' },
        { value: 1, label: '1 - Chute 30-50% ou nadir 10-19' },
        { value: 0, label: '0 - Chute < 30% ou nadir < 10' }
      ]},
      { id: 'timing', label: 'Délai d\'apparition', type: 'select', options: [
        { value: 2, label: '2 - J5-10 ou ≤ J1 si héparine < 30j' },
        { value: 1, label: '1 - Probable J5-10 ou ≤ J1 si héparine 30-100j' },
        { value: 0, label: '0 - < J4 sans exposition récente' }
      ]},
      { id: 'thrombosis', label: 'Thrombose/autre', type: 'select', options: [
        { value: 2, label: '2 - Nouvelle thrombose, nécrose cutanée, réaction systémique' },
        { value: 1, label: '1 - Extension thrombose, suspicion thrombose' },
        { value: 0, label: '0 - Aucune' }
      ]},
      { id: 'otherCause', label: 'Autre cause de thrombopénie', type: 'select', options: [
        { value: 2, label: '2 - Aucune évidente' },
        { value: 1, label: '1 - Possible' },
        { value: 0, label: '0 - Autre cause évidente' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `4T: ${score}`, detail: 'Probabilité faible (< 5%). TIH peu probable' };
      if (score <= 5) return { level: 'warning', text: `4T: ${score}`, detail: 'Probabilité intermédiaire (14-64%). Anticorps anti-PF4' };
      return { level: 'danger', text: `4T: ${score}`, detail: 'Probabilité forte (> 64%). Arrêt héparine, anticoagulation alternative' };
    },
    formula: '4T = Thrombopénie + Timing + Thrombose + auTre cause'
  },

  padoue: {
    name: 'Score de Padoue',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Risque thrombose patient hospitalisé',
    color: 'red',
    fields: [
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 3 },
      { id: 'thrombophilia', label: 'Thrombophilie connue', type: 'checkbox', points: 3 },
      { id: 'immobilization', label: 'Réduction mobilité', type: 'checkbox', points: 3 },
      { id: 'vteHistory', label: 'ATCD MTEV', type: 'checkbox', points: 3 },
      { id: 'trauma', label: 'Traumatisme/chirurgie ≤ 1 mois', type: 'checkbox', points: 2 },
      { id: 'age70', label: 'Âge ≥ 70 ans', type: 'checkbox', points: 1 },
      { id: 'heartFailure', label: 'Insuffisance cardiaque/respiratoire', type: 'checkbox', points: 1 },
      { id: 'idm', label: 'IDM ou AVC aigu', type: 'checkbox', points: 1 },
      { id: 'infection', label: 'Infection aiguë/rhumatisme', type: 'checkbox', points: 1 },
      { id: 'obesity', label: 'Obésité (IMC ≥ 30)', type: 'checkbox', points: 1 },
      { id: 'hormones', label: 'Traitement hormonal', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.cancer) total += 3;
      if (values.thrombophilia) total += 3;
      if (values.immobilization) total += 3;
      if (values.vteHistory) total += 3;
      if (values.trauma) total += 2;
      if (values.age70) total += 1;
      if (values.heartFailure) total += 1;
      if (values.idm) total += 1;
      if (values.infection) total += 1;
      if (values.obesity) total += 1;
      if (values.hormones) total += 1;
      return { score: total, max: 20 };
    },
    interpret: (score) => {
      if (score < 4) return { level: 'success', text: `Padoue ${score}`, detail: 'Risque faible. Pas de prophylaxie systématique' };
      return { level: 'warning', text: `Padoue ${score}`, detail: 'Risque élevé. Thromboprophylaxie recommandée' };
    },
    formula: 'Padoue ≥ 4 = indication thromboprophylaxie'
  },

  caprini: {
    name: 'Score de Caprini',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Risque MTEV post-chirurgical',
    color: 'red',
    fields: [
      { id: 'age41', label: 'Âge 41-60', type: 'checkbox', points: 1 },
      { id: 'age61', label: 'Âge 61-74', type: 'checkbox', points: 2 },
      { id: 'age75', label: 'Âge ≥ 75', type: 'checkbox', points: 3 },
      { id: 'minorSurgery', label: 'Chirurgie mineure', type: 'checkbox', points: 1 },
      { id: 'majorSurgery', label: 'Chirurgie majeure', type: 'checkbox', points: 2 },
      { id: 'obesity', label: 'IMC > 25', type: 'checkbox', points: 1 },
      { id: 'edema', label: 'Œdèmes MI', type: 'checkbox', points: 1 },
      { id: 'varices', label: 'Varices', type: 'checkbox', points: 1 },
      { id: 'sepsis', label: 'Sepsis < 1 mois', type: 'checkbox', points: 1 },
      { id: 'pneumonia', label: 'Pneumopathie < 1 mois', type: 'checkbox', points: 1 },
      { id: 'pregnancy', label: 'Grossesse/post-partum', type: 'checkbox', points: 1 },
      { id: 'contraception', label: 'Contraception/THS', type: 'checkbox', points: 1 },
      { id: 'bed', label: 'Alitement > 72h', type: 'checkbox', points: 1 },
      { id: 'cancer', label: 'Cancer présent/passé', type: 'checkbox', points: 2 },
      { id: 'central', label: 'Cathéter veineux central', type: 'checkbox', points: 2 },
      { id: 'vteHistory', label: 'ATCD TVP/EP', type: 'checkbox', points: 3 },
      { id: 'familyHistory', label: 'ATCD familial TVP/EP', type: 'checkbox', points: 3 },
      { id: 'factorV', label: 'Facteur V Leiden', type: 'checkbox', points: 3 },
      { id: 'stroke', label: 'AVC < 1 mois', type: 'checkbox', points: 5 },
      { id: 'fracture', label: 'Fracture hanche/MI < 1 mois', type: 'checkbox', points: 5 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.age41) total += 1;
      if (values.age61) total += 2;
      if (values.age75) total += 3;
      if (values.minorSurgery) total += 1;
      if (values.majorSurgery) total += 2;
      if (values.obesity) total += 1;
      if (values.edema) total += 1;
      if (values.varices) total += 1;
      if (values.sepsis) total += 1;
      if (values.pneumonia) total += 1;
      if (values.pregnancy) total += 1;
      if (values.contraception) total += 1;
      if (values.bed) total += 1;
      if (values.cancer) total += 2;
      if (values.central) total += 2;
      if (values.vteHistory) total += 3;
      if (values.familyHistory) total += 3;
      if (values.factorV) total += 3;
      if (values.stroke) total += 5;
      if (values.fracture) total += 5;
      return { score: total, max: 'variable' };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `Caprini ${score}`, detail: 'Très faible risque (< 0.5%)' };
      if (score <= 2) return { level: 'success', text: `Caprini ${score}`, detail: 'Risque faible (0.5-1.5%). Déambulation précoce' };
      if (score <= 4) return { level: 'warning', text: `Caprini ${score}`, detail: 'Risque modéré (3%). HBPM prophylactique' };
      return { level: 'danger', text: `Caprini ${score}`, detail: 'Risque élevé (6%). HBPM + compression' };
    },
    formula: 'Caprini: somme des facteurs de risque thromboembolique'
  }
};

// Export all additional specialty scores
export const ADDITIONAL_SPECIALTY_SCORES = {
  ...ONCOLOGY_SCORES,
  ...ORTHOPEDIC_SCORES,
  ...RHEUMATOLOGY_SCORES,
  ...TOXICOLOGY_SCORES,
  ...OBSTETRIC_SCORES,
  ...HEMATOLOGY_SCORES
};
